import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Avatar,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

//... Icon imports
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PeopleIcon from "@mui/icons-material/People";
import AvatarGroup from "@mui/material/AvatarGroup";

//... Other imports
import Helper from "../../common/Helper";
import RouteConstants from "../../common/RouteConstants";
import TitleTooltip from "../../sharedComponents/Tooltip/TitleTooltip";
import TeamPlayersPanel from "./TeamPlayersPanel";

const GameCard = ({ selectedEventId, video, openNewTab, handlePlay }) => {
  const navigate = useNavigate();
  const {
    gameName,
    url,
    team1Name,
    team2Name,
    team1PlayersList = [],
    team2PlayersList = [],
  } = video;

  const uniquePlayers = Array.from(
    new Map(
      [...team1PlayersList, ...team2PlayersList].map((p) => [p.id, p])
    ).values()
  );

  const viewPlayerProfile = (playerId) => {
    navigate(
      `/${selectedEventId}${RouteConstants.VIEW_PLAYER_PROFILE}/${playerId}`
    );
  };

  return (
    <Card
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        background: "linear-gradient(180deg, #ffffff, #f9fafb)",
        border: "1px solid #eef2f7",
        boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
        transition: "all 0.25s ease",
        "&:hover": {
          transform: "translateY(-6px) scale(1.01)",
          boxShadow: "0 14px 30px rgba(0,0,0,0.10)",
        },
      }}
    >
      <CardContent sx={{ pb: "16px !important" }}>
        {/* HEADER */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1.5,
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 800,
                color: "#0f172a",
                lineHeight: 1.2,
                mb: 0.5,
              }}
            >
              {gameName}
            </Typography>

            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#475569",
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.6,
              }}
            >
              <Box sx={{ color: "#2563eb", fontWeight: 700 }}>{team1Name}</Box>

              <Box sx={{ color: "#94a3b8", fontWeight: 700 }}>vs</Box>

              <Box sx={{ color: "#ef4444", fontWeight: 700 }}>{team2Name}</Box>
            </Typography>
          </Box>

          {Helper.isHttpVideoURL(url) && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                justifyContent: "center",
                gap: 0.5,
                flexShrink: 0,
              }}
            >
              <IconButton
                size="small"
                onClick={() => openNewTab(url)}
                sx={{
                  background: "#f1f5f9",
                  borderRadius: 2,
                  "&:hover": {
                    background: "#e2e8f0",
                    transform: "scale(1.05)",
                  },
                }}
              >
                <TitleTooltip title="Open video in new tab">
                  <OpenInNewIcon fontSize="small" />
                </TitleTooltip>
              </IconButton>
            </Box>
          )}
        </Box>

        {/* THUMBNAIL */}
        <Box
          onClick={() => handlePlay(video)}
          sx={{
            height: 160,
            borderRadius: 2,
            background: "linear-gradient(135deg, #1e293b, #334155)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            position: "relative",
            overflow: "hidden",
            "&:hover .playIcon": {
              transform: "scale(1.2)",
            },
          }}
        >
          <PlayCircleOutlineIcon
            className="playIcon"
            sx={{
              fontSize: 54,
              color: "#fff",
              transition: "0.3s",
            }}
          />
        </Box>

        {/* FOOTER */}
        <Box
          sx={{
            mt: 2,
            pt: 1.5,
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* COUNT */}
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: "#4338ca",
              backgroundColor: "#eef2ff",
              px: 1.2,
              py: 0.4,
              borderRadius: "999px",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <PeopleIcon sx={{ fontSize: 16 }} />
            {uniquePlayers.length} Players
          </Typography>
          <TitleTooltip
            enterDelay={300}
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: "#ffffff",
                  color: "#111827",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
                  borderRadius: 3,
                  p: 0,
                  maxWidth: "none",
                },
              },
            }}
            title={
              <Box
                sx={{
                  width: 520,
                  borderRadius: 3,
                  border: "1px solid #e5e7eb",
                  background: "#f8fafc",
                  overflow: "hidden",
                }}
              >
                <TeamPlayersPanel
                  title={team1Name}
                  label="Team 1"
                  players={team1PlayersList}
                  color="#1d428a"
                  onPlayerClick={viewPlayerProfile}
                />

                <TeamPlayersPanel
                  title={team2Name}
                  label="Team 2"
                  players={team2PlayersList}
                  color="#dc2626"
                  onPlayerClick={viewPlayerProfile}
                />
              </Box>
            }
          >
            <AvatarGroup
              max={5}
              sx={{
                cursor: "pointer",
                "& .MuiAvatar-root": {
                  width: 30,
                  height: 30,
                  fontSize: 12,
                  border: "2px solid #fff",
                },
              }}
            >
              {uniquePlayers.map((p) => (
                <Avatar
                  key={p.id}
                  src={p.headShotUrl || ""}
                  sx={{ bgcolor: "#1d428a" }}
                >
                  {!p.headShotUrl && Helper.GetNameInitials(p.name)}
                </Avatar>
              ))}
            </AvatarGroup>
          </TitleTooltip>
        </Box>
      </CardContent>
    </Card>
  );
};

export default GameCard;
